import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download, Filter, Calendar, User, FileSpreadsheet } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { generateRetentionPDF } from '@/utils/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const [retentions, setRetentions] = useState([]);
  const [filteredRetentions, setFilteredRetentions] = useState([]);
  const [providers, setProviders] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  
  // Filtros
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [fechaDesde, setFechaDesde] = useState({ dia: '', mes: '', anio: '' });
  const [fechaHasta, setFechaHasta] = useState({ dia: '', mes: '', anio: '' });
  const [tipoRetencion, setTipoRetencion] = useState('all');

  useEffect(() => {
    fetchRetentions();
    fetchProviders();
    fetchEmpresa();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [retentions, selectedProvider, fechaDesde, fechaHasta, tipoRetencion]);

  const fetchRetentions = async () => {
    try {
      const response = await axios.get(`${API}/retenciones`);
      setRetentions(response.data);
    } catch (error) {
      console.error('Error fetching retentions:', error);
      toast.error('Error al cargar retenciones');
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/proveedores`);
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchEmpresa = async () => {
    try {
      const response = await axios.get(`${API}/empresa`);
      setEmpresa(response.data);
    } catch (error) {
      console.error('Error fetching empresa:', error);
    }
  };

  const buildDate = (fecha) => {
    if (!fecha.dia || !fecha.mes || !fecha.anio) return null;
    const anio = '20' + fecha.anio.padStart(2, '0');
    const mes = fecha.mes.padStart(2, '0');
    const dia = fecha.dia.padStart(2, '0');
    return new Date(`${anio}-${mes}-${dia}`);
  };

  const applyFilters = () => {
    let filtered = [...retentions];

    // Filtrar por proveedor
    if (selectedProvider !== 'all') {
      filtered = filtered.filter(r => r.proveedor_id === selectedProvider);
    }

    // Filtrar por tipo
    if (tipoRetencion !== 'all') {
      filtered = filtered.filter(r => r.tipo === tipoRetencion);
    }

    // Filtrar por fecha desde
    const desde = buildDate(fechaDesde);
    if (desde) {
      filtered = filtered.filter(r => new Date(r.fecha) >= desde);
    }

    // Filtrar por fecha hasta
    const hasta = buildDate(fechaHasta);
    if (hasta) {
      hasta.setHours(23, 59, 59, 999);
      filtered = filtered.filter(r => new Date(r.fecha) <= hasta);
    }

    setFilteredRetentions(filtered);
  };

  const clearFilters = () => {
    setSelectedProvider('all');
    setTipoRetencion('all');
    setFechaDesde({ dia: '', mes: '', anio: '' });
    setFechaHasta({ dia: '', mes: '', anio: '' });
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const anio = date.getFullYear();
    return `${dia}/${mes}/${anio}`;
  };

  const getProviderName = () => {
    if (selectedProvider === 'all') return 'Todos los proveedores';
    const provider = providers.find(p => p.id === selectedProvider);
    return provider ? provider.nombre : 'Proveedor';
  };

  const getDateRangeText = () => {
    const desde = buildDate(fechaDesde);
    const hasta = buildDate(fechaHasta);
    let text = '';
    if (desde) text += `Desde: ${formatDate(desde.toISOString())}`;
    if (hasta) text += (text ? ' - ' : '') + `Hasta: ${formatDate(hasta.toISOString())}`;
    return text || 'Todas las fechas';
  };

  // Exportar reporte en lista (tabla resumen)
  const exportReportList = () => {
    if (filteredRetentions.length === 0) {
      toast.error('No hay retenciones para exportar');
      return;
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'letter'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;

    // Título
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('REPORTE DE RETENCIONES', pageWidth / 2, 15, { align: 'center' });

    // Información de la empresa
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(empresa?.nombre || 'Empresa', pageWidth / 2, 22, { align: 'center' });
    doc.text(`RIF: ${empresa?.rif || ''}`, pageWidth / 2, 27, { align: 'center' });

    // Filtros aplicados
    doc.setFontSize(9);
    doc.text(`Proveedor: ${getProviderName()}`, margin, 35);
    doc.text(`Período: ${getDateRangeText()}`, margin, 40);
    doc.text(`Tipo: ${tipoRetencion === 'all' ? 'Todos' : tipoRetencion}`, margin, 45);
    doc.text(`Total de registros: ${filteredRetentions.length}`, pageWidth - margin - 50, 35);

    // Tabla de retenciones
    const tableData = filteredRetentions.map(r => [
      r.numero_comprobante,
      r.numero_factura || '-',
      formatDate(r.fecha),
      r.proveedor_nombre,
      r.proveedor_rif,
      r.tipo,
      formatCurrency(r.subtotal || 0),
      `${r.porcentaje_retencion}%`,
      formatCurrency(r.monto_retenido)
    ]);

    // Calcular totales
    const totalRetenido = filteredRetentions.reduce((sum, r) => sum + (parseFloat(r.monto_retenido) || 0), 0);
    const totalBase = filteredRetentions.reduce((sum, r) => sum + (parseFloat(r.subtotal) || 0), 0);

    autoTable(doc, {
      startY: 50,
      head: [['Comprobante', 'Nº Factura', 'Fecha', 'Proveedor', 'RIF', 'Tipo', 'Base Imponible', '% Ret.', 'Monto Retenido']],
      body: tableData,
      foot: [[
        { content: 'TOTALES', colSpan: 6, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: formatCurrency(totalBase), styles: { fontStyle: 'bold', halign: 'right' } },
        { content: '', styles: { halign: 'center' } },
        { content: formatCurrency(totalRetenido), styles: { fontStyle: 'bold', halign: 'right' } }
      ]],
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [30, 58, 95], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
      columnStyles: {
        0: { cellWidth: 28, halign: 'center' },
        1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 45 },
        4: { cellWidth: 25, halign: 'center', fontStyle: 'mono' },
        5: { cellWidth: 15, halign: 'center' },
        6: { cellWidth: 28, halign: 'right' },
        7: { cellWidth: 15, halign: 'center' },
        8: { cellWidth: 28, halign: 'right' },
      },
    });

    // Pie de página
    const finalY = doc.lastAutoTable?.finalY || 200;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(`Generado el: ${new Date().toLocaleDateString('es-VE')} ${new Date().toLocaleTimeString('es-VE')}`, margin, finalY + 10);

    // Guardar
    const providerSlug = selectedProvider === 'all' ? 'Todos' : getProviderName().replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(`Reporte_Retenciones_${providerSlug}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Reporte exportado exitosamente');
  };

  // Exportar cada retención como PDF individual en un solo archivo
  const exportIndividualPDFs = async () => {
    if (filteredRetentions.length === 0) {
      toast.error('No hay retenciones para exportar');
      return;
    }

    toast.info(`Generando ${filteredRetentions.length} comprobantes...`);

    // Crear un PDF con todas las retenciones
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'letter'
    });

    for (let i = 0; i < filteredRetentions.length; i++) {
      const retention = filteredRetentions[i];
      
      // Obtener datos del proveedor
      const provider = providers.find(p => p.id === retention.proveedor_id) || {
        nombre: retention.proveedor_nombre,
        rif: retention.proveedor_rif,
        direccion: ''
      };

      // Generar el PDF individual y obtener el doc
      const individualDoc = generateRetentionPDF(retention, provider, empresa, { preview: true });
      
      // Añadir página si no es la primera
      if (i > 0) {
        doc.addPage('letter', 'landscape');
      }

      // Copiar el contenido del PDF individual al documento principal
      const pdfData = individualDoc.output('arraybuffer');
      
      // Simplemente generamos cada uno en el documento principal
      // Usamos la misma lógica del generador
      if (retention.tipo === 'ISLR') {
        generateISLRPage(doc, retention, provider, empresa);
      } else {
        generateIVAPage(doc, retention, provider, empresa);
      }
    }

    const providerSlug = selectedProvider === 'all' ? 'Todos' : getProviderName().replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(`Comprobantes_${providerSlug}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success(`${filteredRetentions.length} comprobantes exportados en un archivo`);
  };

  // Funciones auxiliares para generar páginas IVA e ISLR
  const generateIVAPage = (doc, retention, provider, empresa) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const black = [0, 0, 0];
    const headerBg = [220, 220, 220];

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('COMPROBANTE DE RETENCION DE I.V.A.', pageWidth / 2, 12, { align: 'center' });

    let y = 18;

    // Nro. Comprobante y Fecha
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Nro. Comprobante', margin + 1, y);
    doc.rect(margin, y + 1, 32, 8);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(retention.numero_comprobante || '', margin + 1, y + 6);

    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Fecha:', margin + 35, y);
    doc.rect(margin + 33, y + 1, 22, 8);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(formatDate(retention.fecha), margin + 35, y + 6);

    // Simplificado para el reporte
    y += 12;
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Agente de Retención:', margin + 1, y);
    doc.rect(margin, y + 1, pageWidth - margin * 2, 7);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${empresa?.nombre || ''} - RIF: ${empresa?.rif || ''}`, margin + 1, y + 5);

    y += 10;
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Sujeto Retenido:', margin + 1, y);
    doc.rect(margin, y + 1, pageWidth - margin * 2, 7);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${provider?.nombre || ''} - RIF: ${provider?.rif || ''}`, margin + 1, y + 5);

    y += 12;
    const tableData = [[
      '1',
      formatDate(retention.fecha_factura),
      retention.numero_factura || '',
      retention.numero_control || '',
      formatCurrency(retention.monto_total_factura),
      formatCurrency(retention.subtotal),
      '16%',
      `${retention.porcentaje_retencion}%`,
      formatCurrency(retention.iva_factura),
      formatCurrency(retention.monto_retenido)
    ]];

    autoTable(doc, {
      startY: y,
      head: [['Op', 'Fecha', 'Nº Fact', 'Nº Control', 'Total', 'Base Imp.', '% Alic', '% Ret', 'IVA', 'Retenido']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: headerBg, textColor: black, fontStyle: 'bold', halign: 'center', fontSize: 6 },
    });

    const finalY = doc.lastAutoTable?.finalY || y + 20;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`TOTAL RETENIDO: ${formatCurrency(retention.monto_retenido)}`, pageWidth - margin - 5, finalY + 8, { align: 'right' });
  };

  const generateISLRPage = (doc, retention, provider, empresa) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const black = [0, 0, 0];
    const headerBg = [200, 210, 230];

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(margin, 10, pageWidth - margin * 2, 18);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('COMPROBANTE DE RETENCION DE IMPUESTO SOBRE LA RENTA', pageWidth / 2, 17, { align: 'center' });
    doc.text('DECRETO 1808', pageWidth / 2, 23, { align: 'center' });

    let y = 32;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('NUMERO DE COMPROBANTE: ', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(retention.numero_comprobante || '', margin + 42, y);

    doc.setFont('helvetica', 'bold');
    doc.text('FECHA: ', pageWidth - margin - 40, y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(retention.fecha), pageWidth - margin - 25, y);

    y += 8;
    doc.setFillColor(...headerBg);
    doc.rect(margin, y, pageWidth - margin * 2, 6, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('AGENTE DE RETENCION', margin + 2, y + 4);

    y += 6;
    doc.rect(margin, y, pageWidth - margin * 2, 8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${empresa?.nombre || ''} - RIF: ${empresa?.rif || ''}`, margin + 2, y + 5);

    y += 10;
    doc.setFillColor(...headerBg);
    doc.rect(margin, y, pageWidth - margin * 2, 6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('CONTRIBUYENTE', margin + 2, y + 4);

    y += 6;
    doc.rect(margin, y, pageWidth - margin * 2, 8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${provider?.nombre || ''} - RIF: ${provider?.rif || ''}`, margin + 2, y + 5);

    y += 14;
    const montoTotal = parseFloat(retention.monto_total_factura) || 0;
    const montoRetenido = parseFloat(retention.monto_retenido) || 0;
    const totalPagar = montoTotal - montoRetenido;

    const tableData = [[
      formatDate(retention.fecha_factura),
      retention.numero_factura || '',
      retention.numero_control || '',
      'FACTURA',
      formatCurrency(montoTotal),
      `${retention.porcentaje_retencion}%`,
      formatCurrency(montoRetenido),
      formatCurrency(totalPagar)
    ]];

    autoTable(doc, {
      startY: y,
      head: [['Fecha', 'Número', 'Control', 'Tipo', 'Monto Total', '% Ret', 'Retenido', 'A Pagar']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: headerBg, textColor: black, fontStyle: 'bold', halign: 'center' },
    });

    const finalY = doc.lastAutoTable?.finalY || y + 20;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`TOTAL RETENIDO: ${formatCurrency(montoRetenido)}`, pageWidth - margin - 5, finalY + 8, { align: 'right' });
  };

  const DateInput = ({ label, value, onChange }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-1 mt-1">
        <Input
          type="text"
          inputMode="numeric"
          maxLength="2"
          placeholder="DD"
          value={value.dia}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 2);
            if (parseInt(val) <= 31 || val === '') onChange({ ...value, dia: val });
          }}
          className="w-12 text-center font-mono text-sm p-1"
        />
        <span className="text-slate-400">/</span>
        <Input
          type="text"
          inputMode="numeric"
          maxLength="2"
          placeholder="MM"
          value={value.mes}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 2);
            if (parseInt(val) <= 12 || val === '') onChange({ ...value, mes: val });
          }}
          className="w-12 text-center font-mono text-sm p-1"
        />
        <span className="text-slate-400">/</span>
        <span className="bg-slate-100 border border-slate-300 rounded-l-md px-1 py-1 text-slate-600 font-mono text-sm">20</span>
        <Input
          type="text"
          inputMode="numeric"
          maxLength="2"
          placeholder="AA"
          value={value.anio}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 2);
            onChange({ ...value, anio: val });
          }}
          className="w-12 text-center font-mono text-sm p-1 rounded-l-none border-l-0"
        />
      </div>
    </div>
  );

  return (
    <div className="p-8" data-testid="reports-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 font-heading">Reportes</h1>
        <p className="text-sm text-slate-600 mt-2">Genera reportes de retenciones por proveedor y período</p>
      </div>

      {/* Filtros */}
      <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="text-blue-600" size={20} />
          <h2 className="text-lg font-semibold text-slate-900">Filtros</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Proveedor */}
          <div>
            <Label>Proveedor</Label>
            <Select value={selectedProvider} onValueChange={setSelectedProvider}>
              <SelectTrigger data-testid="provider-filter">
                <SelectValue placeholder="Seleccionar proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los proveedores</SelectItem>
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tipo de Retención */}
          <div>
            <Label>Tipo de Retención</Label>
            <Select value={tipoRetencion} onValueChange={setTipoRetencion}>
              <SelectTrigger data-testid="type-filter">
                <SelectValue placeholder="Seleccionar tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="IVA">IVA</SelectItem>
                <SelectItem value="ISLR">ISLR</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Fecha Desde */}
          <DateInput 
            label="Fecha Desde" 
            value={fechaDesde} 
            onChange={setFechaDesde}
          />

          {/* Fecha Hasta */}
          <DateInput 
            label="Fecha Hasta" 
            value={fechaHasta} 
            onChange={setFechaHasta}
          />
        </div>

        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={clearFilters}>
            Limpiar Filtros
          </Button>
        </div>
      </Card>

      {/* Resultados y Exportación */}
      <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Resultados</h2>
            <p className="text-sm text-slate-500">
              {filteredRetentions.length} retenciones encontradas
              {selectedProvider !== 'all' && ` para ${getProviderName()}`}
            </p>
          </div>

          <div className="flex gap-2">
            <Button 
              onClick={exportReportList}
              disabled={filteredRetentions.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="export-list-btn"
            >
              <FileSpreadsheet size={16} className="mr-2" />
              Exportar Lista
            </Button>
            <Button 
              onClick={exportIndividualPDFs}
              disabled={filteredRetentions.length === 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="export-individual-btn"
            >
              <FileText size={16} className="mr-2" />
              Exportar Comprobantes
            </Button>
          </div>
        </div>

        {/* Resumen */}
        {filteredRetentions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="text-xs text-slate-500 uppercase">Total Retenciones</p>
              <p className="text-2xl font-bold text-slate-900">{filteredRetentions.length}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Total Base Imponible</p>
              <p className="text-2xl font-bold text-blue-600">
                Bs.S {formatCurrency(filteredRetentions.reduce((sum, r) => sum + (parseFloat(r.subtotal) || 0), 0))}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Total Retenido</p>
              <p className="text-2xl font-bold text-green-600">
                Bs.S {formatCurrency(filteredRetentions.reduce((sum, r) => sum + (parseFloat(r.monto_retenido) || 0), 0))}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Proveedores</p>
              <p className="text-2xl font-bold text-slate-900">
                {new Set(filteredRetentions.map(r => r.proveedor_id)).size}
              </p>
            </div>
          </div>
        )}

        {/* Tabla de resultados */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Comprobante</th>
                <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Nº Factura</th>
                <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Proveedor</th>
                <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500 uppercase">Base Imp.</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500 uppercase">Retenido</th>
              </tr>
            </thead>
            <tbody>
              {filteredRetentions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-slate-500">
                    No hay retenciones que coincidan con los filtros
                  </td>
                </tr>
              ) : (
                filteredRetentions.map((retention) => (
                  <tr key={retention.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="p-3 font-mono text-sm">{retention.numero_comprobante}</td>
                    <td className="p-3 font-mono text-sm">{retention.numero_factura || '-'}</td>
                    <td className="p-3 text-sm">{formatDate(retention.fecha)}</td>
                    <td className="p-3 text-sm">
                      <div className="font-medium">{retention.proveedor_nombre}</div>
                      <div className="text-xs text-slate-500 font-mono">{retention.proveedor_rif}</div>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        retention.tipo === 'IVA' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {retention.tipo}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono text-sm">{formatCurrency(retention.subtotal || 0)}</td>
                    <td className="p-3 text-right font-mono text-sm font-bold">{formatCurrency(retention.monto_retenido)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Reports;
